import React, { useState, useEffect } from 'react';
import { Select, AutoComplete, Space, Tag, Typography } from 'antd';
import { UserOutlined, TeamOutlined, TrophyOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;
const { Text } = Typography;

interface Player {
  id: number;
  name: string;
  steamId: string;
  playerId: string;
  level: number;
  tribe: string;
  tribeId?: string;
  tribeName?: string;
  isOnline: boolean;
  lastOnline: string;
  position?: {
    x: number;
    y: number;
    z: number;
    formatted: string;
  } | string;
  dinoCount?: number;
  health?: number;
  stamina?: number;
  food?: number;
  water?: number;
  weight?: number;
  oxygen?: number;
  craftingSpeed?: number;
  fortitude?: number;
  movementSpeed?: number;
  meleeDamage?: number;
  speed?: number;
}

interface PlayerSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  serverId?: string;
}

const PlayerSelector: React.FC<PlayerSelectorProps> = ({
  value,
  onChange,
  placeholder = "选择玩家或输入SteamID",
  disabled = false,
  serverId
}) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);

  // 加载玩家列表
  const loadPlayers = async () => {
    if (!serverId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/ark-rcon/servers/${serverId}/players`);
      const playersData = response.data?.players || [];
      setPlayers(playersData);
    } catch (error) {
      console.error('加载玩家列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载玩家列表
  useEffect(() => {
    loadPlayers();
  }, [serverId]);

  // 处理搜索
  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
    
    if (!searchText.trim()) {
      setFilteredPlayers(players);
      return;
    }

    const filtered = players.filter(player => 
      player.name.toLowerCase().includes(searchText.toLowerCase()) ||
      player.steamId.includes(searchText) ||
      (player.tribeName && player.tribeName.toLowerCase().includes(searchText.toLowerCase()))
    );
    
    setFilteredPlayers(filtered);
  };

  // 初始化时设置过滤后的玩家列表
  useEffect(() => {
    setFilteredPlayers(players);
  }, [players]);

  // 处理选择
  const handleSelect = (selectedValue: string) => {
    if (onChange) {
      onChange(selectedValue);
    }
  };

  // 处理输入变化
  const handleInputChange = (inputValue: string) => {
    setSearchValue(inputValue);
    if (onChange) {
      onChange(inputValue);
    }
  };

  // 获取显示值
  const getDisplayValue = (steamId: string) => {
    const player = players.find(p => p.steamId === steamId);
    if (player) {
      return `${player.name} (${player.steamId})`;
    }
    return steamId;
  };

  return (
    <AutoComplete
      value={searchValue || getDisplayValue(value || '')}
      onChange={handleInputChange}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '100%' }}
      options={filteredPlayers.map(player => ({
        value: player.steamId,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <UserOutlined style={{ color: '#1890ff' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {player.name}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                SteamID: {player.steamId}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Tag 
                  color={player.tribeName && player.tribeName !== '未加入部落' ? 'purple' : 'default'} 
                  icon={<TeamOutlined />}
                  style={{ fontSize: '11px' }}
                >
                  {player.tribeName || '未加入部落'}
                </Tag>
                <Tag 
                  color={player.dinoCount && player.dinoCount > 0 ? 'blue' : 'default'} 
                  icon={<TrophyOutlined />}
                  style={{ fontSize: '11px' }}
                >
                  恐龙: {player.dinoCount || 0}
                </Tag>
                <Tag 
                  color={player.isOnline ? 'green' : 'red'}
                  style={{ fontSize: '11px' }}
                >
                  {player.isOnline ? '在线' : '离线'}
                </Tag>
              </div>
            </div>
          </div>
        )
      }))}
      filterOption={false}
    />
  );
};

export default PlayerSelector;
